import React from 'react';
import i18n from "I18n";
import config from 'config';

import { CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY, CampaignType, EarningRuleType, LanguageConfig } from 'config/CustomEnums';
import ReactHookFormTitleWithRadio from 'components/reactHookFormComponents/ReactHookFormTitleWithRadio';

import { ProcessEnvConfig } from 'config/ProcessEnvConfig';
import ReactHookFormSwitch from 'components/reactHookFormComponents/ReactHookFormSwitch';
import ReactHookFormTextInput from 'components/reactHookFormComponents/ReactHookFormTextInput';
import { ReactHookFormMarkDown } from 'components/base/CustomMarkDownArea';
import BrandDropdownWithButtonAndPrompt from 'components/campaign/campaignCreation/BrandDropdownWithButtonAndPrompt';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';
import ReactFormTargetCustomerSelector from 'components/campaign/campaignCreation/ReactFormTargetCustomerSelector';
import ReactHookFormMultipleSelector from 'components/reactHookFormComponents/ReactHookFormMultipleSelector';
import PhotoUploaderWithSwitch from 'components/campaign/campaignCreation/PhotoUploaderWithSwitch';
import { getTipsImages } from 'components/campaign/campaignCreation/CampaignStepTwoPreviewConfig';
import ReactHookFormMarkDownArea from 'components/reactHookFormComponents/ReactHookFormMarkDownArea';
import PeriodSection from 'components/base/period/PeriodSection';
import { errorMessage, hasError } from 'components/base/ErrorFieldMessage';
import PeriodAndWeekdaySection from 'components/base/period/PeriodAndWeekdaySection';
import VisiblePeriodSection from 'components/campaign/campaignCreation/VisiblePeriodSection';
import RecurringCampaignSwitch from 'components/campaign/campaignCreation/RecurringCampaignSwitch';
import RecurringCampaignPeriodSelector from 'components/campaign/campaignCreation/RecurringCampaignPeriodSelector';
import RecurringMessages from 'components/campaign/campaignCreation/RecurringMessages';
import { ReactHookFormImageUploader } from 'components/base/ImageUploader';
import CategorySelectorWithPrompt from 'components/campaign/campaignCreation/CategorySelectorWithPrompt';
import VisibleMallSection from 'components/campaign/campaignCreation/VisibleMallSection';

import HomepageBanner from 'assets/images/campaignStreamline/homepage_banner.png';
import HomepageFeatured from 'assets/images/campaignStreamline/homepage_featured.png';
import OfferTab from 'assets/images/campaignStreamline/offer_tab.png';
import OnlyForYou from 'assets/images/campaignStreamline/only_for_you.png';
import { getShowDisplayInFrontend } from 'models/CreateCampaignModel';
import { tierTypeConfig } from 'config/TierTypeConfig';


const targetCustomerType = [
  {
    label: 'All customers',
    value: false,
  },
  {
    label: 'Specific segments',
    value: true,
  }
]

const ActivePeriodPopoverDetail = () => (
  <ul className='campaign-step-2-active-period-popover-detail'>
    <li>
      {"For coupon campaign\nControl when customers can acquire coupons in frontend."}
    </li>
    <li>
      {"For QR code scanning\nControl when customers can scan the QR codes and earn rewards in frontend."}
    </li>
    <li>
      {"For GPS check-in\nControl when customers can check in and earn rewards in frontend."}
    </li>
    <li>
      {"For purchase\nThe purchase date must be within the active period to earn rewards."}
    </li>
    <li>
      {"For referral / new member\nThe sign up date must be within the active period to earn rewards."}
    </li>
    <li>
      {"For birthday\nSystem will issue rewards during the active period in the birth months of customers."}
    </li>
    <li>
      {"For level upgrade\nThe upgrade date must be within the active period to earn rewards."}
    </li>
  </ul>
);
export const campaignCreationGeneralSectionConfig = {
  keys: [
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.general.sectionContent.general_name_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.general.sectionContent.short_description_optional_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.general.sectionContent.related_brand_optional_key,
  ],
  getDisplayKeys: (isExclusive, excludeBlacklistSegment) => {
    if (!tierTypeConfig.targetCustomer) {
      return []
    }
    return [
      CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.is_exclusive_campaign_key,
      CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.exclude_blacklist_segment_switch_key,
      ...(isExclusive ? [
        CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.target_customer_selector_key,
      ] : []),
      ...(excludeBlacklistSegment ? [
        CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.blacklist_segment_selector_key,
      ] : []),
    ]
  },
  section: {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.general.sectionKey,
    sectionTitle: 'general',
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.general.sectionContent.general_name_key,
        component: ReactHookFormTextInput,
        componentProps: {
          valueFieldName: 'generalName',
          withTranslation: true,
          focusKey: 'name',
          title: 'name',
        },
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.general.sectionContent.short_description_optional_key,
        complexComponent: ({ language, setActiveSection, formContext: { setValue }, }) => (
          <ReactHookFormMarkDown
            content={{
              label: i18n.t('short_description_optional', { locale: language }),
              key: `translations.${language}.shortDescription`,
              focus: () => {
                setActiveSection('shortDescription');
              },
            }}
          />
        ),
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.general.sectionContent.related_brand_optional_key,
        complexComponent: ({
          language,
          setActiveSection,
          formContext: { watch },
        }) => {
          if (language === LanguageConfig.english) {
            return (
              <BrandDropdownWithButtonAndPrompt
                labelContainPk={false}
                title={i18n.t('related_brand_optional', { locale: language })}
                defaultFilter={{ isSelectorLoad: true }}
                valueFieldName="linkedBrand"
                hasAddButton={true}
                isPrompt={true}
                promptConfig={{
                  title: 'Go to create brand?',
                  message: `You will leave the campaign creation process. After you create a new brand, you can back to this page.`,
                  button: 'Create Brand',
                  refreshButton: 'Done and refresh',
                  action: () => {
                    window.open('/brands/create', '_blank');
                  },
                }}
                onFocus={() => setActiveSection('relatedBrand')}
              />
            );
          }
          return (
            <div className="brand-select-dropdown-container">
              <CustomTitleLabel
                title={i18n.t('related_brand_optional', { locale: language })}
              />
              <label>{watch('linkedBrand')?.name || '-'}</label>
            </div>
          );
        },
      },
    ]
  }
}
export const campaignCreationTargetCustomerSectionConfig = {
  keys: [
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.is_exclusive_campaign_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.target_customer_selector_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.exclude_blacklist_segment_switch_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.blacklist_segment_selector_key,
  ],
  section: {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionKey,
    hideInOtherLanguages: true,
    sectionTitle: 'TARGET CUSTOMER',
    description: 'Select the target customers if this earning rule is exclusively for a specific segment, or customer level. By default, the earning rule applies to everyone.',
    descriptionClass: 'tips-message',
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.is_exclusive_campaign_key,
        component: ReactHookFormTitleWithRadio,
        componentProps: {
          title: 'Target customer',
          options: targetCustomerType,
          valueFieldName: 'isExclusive',
        },
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.target_customer_selector_key,
        complexComponent: ({ formContext: { watch } }) => (
          <ReactFormTargetCustomerSelector
            selectorTypeFieldName={'generalType'}
          />
        ),
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.exclude_blacklist_segment_switch_key,
        complexComponent: ({ formContext: { watch } }) => (
          <ReactHookFormSwitch
            valueFieldName={"excludeBlacklistSegment"}
            title={'Blacklist customers by segments'}
            tips={'If a customer is targeted and blacklisted at the same time, he cannot view or participate the campaign in the end.'}
          />
        )
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.targetCustomers.sectionContent.blacklist_segment_selector_key,
        component: ReactHookFormMultipleSelector,
        componentProps: {
          valueFieldName: "blacklistSegments",
          loadSourceDataAction: (dispatch) => {
            dispatch({ type: 'segments/getAllBlacklistSegmentList', payload: {} });
          },
          getSourceDataFromState: (state) => state.segments.allBlackListSegmentList,
          title: "Blacklist segment",
          custom: {
            customItem: (item) => `[ID: ${item.pk}] ${item.name}`,
          },
          sortable: false
        },
      },
    ],
  },
}
export const campaignCreationKeyVisualSectionConfig = {
  keys: [
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.cover_photo_upload_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.other_photo_upload_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.square_cover_photo_upload_key,
  ],
  getDisplayKeys: (isExclusive) => {
    return [
      CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.cover_photo_upload_key,
      CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.other_photo_upload_key,
      ...(isExclusive ? [CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.square_cover_photo_upload_key] : []),
    ]
  },
  section: {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionKey,
    sectionTitle: 'key_visual',
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.cover_photo_upload_key,
        component: PhotoUploaderWithSwitch,
        componentProps: {
          withTranslation: true,
          title: 'cover_photo',
          tips: 'cover_photo_suggest_with_variable',
          wordTranslationVariable: config.campaignRectangleSize,
          tranlationFieldPrefix: 'translations',
          photoFieldName: 'coverPhoto',
          activeSectionKey: 'coverPhoto',
          switchConfig: {
            fieldName: 'applyCoverPhotoToAllLanguage',
            title: 'Apply to all language',
          },
          uploaderAspect: 900 / config.campaignRectangleSize.minHeight,
          minWidth: 900,
          minHeight: config.campaignRectangleSize.minHeight,
        },
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.other_photo_upload_key,
        component: PhotoUploaderWithSwitch,
        componentProps: {
          withTranslation: true,
          title: 'other_photos',
          tips: 'cover_photo_suggest_with_variable',
          wordTranslationVariable: config.campaignRectangleSize,
          tranlationFieldPrefix: 'translations',
          photoFieldName: 'detailPhotos',
          activeSectionKey: 'otherPhotos',
          switchConfig: {
            fieldName: 'applyDetailPhotosToAllLanguage',
            title: 'Apply to all language',
          },
          uploaderAspect: 900 / config.campaignRectangleSize.minHeight,
          minWidth: 900,
          minHeight: config.campaignRectangleSize.minHeight,
          maxImageNum: 4,
        },
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.keyVisual.sectionContent.square_cover_photo_upload_key,
        component: PhotoUploaderWithSwitch,
        componentProps: {
          withTranslation: true,
          title: 'campaign.square_cover_photo',
          titlePopoverImage: OnlyForYou,
          tips: 'square_cover_photo_suggest_with_variable',
          wordTranslationVariable: config.campaignSquareSize,
          tranlationFieldPrefix: 'translations',
          photoFieldName: 'squareCoverPhoto',
          activeSectionKey: 'squareCoverPhoto',
          switchConfig: {
            fieldName: 'applySquareCoverPhotoToAllLanguage',
            title: 'Apply to all language',
          },
          uploaderAspect:
            config.campaignSquareSize.minWidth /
            config.campaignSquareSize.minHeight,
        },
      },
    ]
  }
}
export const campaignCreationInstructionSectionConfig = {
  keys: [
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.instruction.sectionContent.instruction_section_key,
  ],
  section: {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.instruction.sectionKey,
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.instruction.sectionContent.instruction_section_key,
        complexComponent: ({
          setActiveSection,
          language,
          formContext: { watch },
        }) => {
          const tipsImageConfig = getTipsImages(watch('campaignType'));
          return (
            <ReactHookFormMarkDownArea
              hasTranslation
              areaTitle="instruction"
              focusChange={setActiveSection}
              language={language}
              titleConfig={{
                label: 'instruction_section_title',
                valueFieldName:
                  'translations.{{language}}.instructionSectionTitle',
                disableFocusChange: false,
                focusChangeKey: 'instructionTitle',
                tips: tipsImageConfig.instructionTitle,
              }}
              contentConfig={{
                label: 'instruction_section_content',
                valueFieldName:
                  'translations.{{language}}.instructionSectionContent',
                disableFocusChange: false,
                focusChangeKey: 'instructionContent',
                tips: tipsImageConfig.instructionContent,
              }}
            />
          );
        },
      },
    ],
  },
}
export const campaignCreationDetailSectionConfig = {
  keys: [
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.detail.sectionContent.detail_section_key,
  ],
  section: {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.detail.sectionKey,
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.detail.sectionContent.detail_section_key,
        complexComponent: ({
          setActiveSection,
          language,
          formContext: { watch },
        }) => {
          const tipsImageConfig = getTipsImages(watch('campaignType'));
          return (
            <ReactHookFormMarkDownArea
              focusChange={setActiveSection}
              language={language}
              areaTitle="detail"
              titleConfig={{
                label: 'detail_section_title',
                valueFieldName:
                  'translations.{{language}}.detailSectionTitle',
                disableFocusChange: false,
                focusChangeKey: 'detailsTitle',
                tips: tipsImageConfig.detailsTitle,
              }}
              contentConfig={{
                label: 'detail_section_content',
                valueFieldName:
                  'translations.{{language}}.detailSectionContent',
                disableFocusChange: false,
                focusChangeKey: 'detailsContent',
                tips: tipsImageConfig.detailsContent,
              }}
              hasTranslation
            />
          );
        },
      },
    ],
  },
}
export const campaignCreationCampaignListingSectionConfig = {
  keys: [
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.homepage_banner_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.is_featured_campaign_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.show_in_offer_tab_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.banner_visible_period_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.banner_image_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.banner_display_order_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.campaign_category_selector_key,
  ],
  getDisplayKeys: (haveHomepageBanner, showInOfferTab ) => {
    return [
      'homepage_banner_key',
      ...(haveHomepageBanner ? [
        'banner_visible_period_key',
        'banner_image_key',
        'banner_display_order_key'
      ] : []),
      'is_featured_campaign_key',
      'show_in_offer_tab_key',
      ...(showInOfferTab ? ['campaign_category_selector_key'] : []),
    ]
  },
  section: {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionKey,
    hideInOtherLanguages: true,
    sectionTitle: 'CAMPAIGN LISTING SECTION',
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.homepage_banner_key,
        component: ReactHookFormSwitch,
        componentProps: {
          title: "Homepage - BANNER",
          valueFieldName: 'haveHomepageBanner',
          titlePopOverImage: HomepageBanner,
        },
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.banner_visible_period_key,
        complexComponent: ({ formContext: { watch, setValue, formState: { errors } } }) => {
          const followCampaignField = 'banner.visiblePeriodFollowLinkedCampaign';
          const startDateField = 'banner.visiblePeriodStartDate';
          const endDateField = 'banner.visiblePeriodEndDate';

          return (
            <PeriodSection
              switchButtonTitle="Banner visible period follow campaign visible period"
              isAlwaysPeriod={watch(followCampaignField)}
              switchOnChange={(value) => {
                setValue(followCampaignField, value, { shouldDirty: true });
              }}
              startDate={watch(startDateField)}
              startDateChange={(date) => {
                setValue(startDateField, date, { shouldDirty: true });
              }}
              startErrorObject={{
                id: startDateField,
                error: hasError(errors, startDateField),
                message: errorMessage(errors, startDateField),
              }}
              endDate={watch(endDateField)}
              endDateChange={(date) => {
                setValue(endDateField, date, { shouldDirty: true });
              }}
              endErrorObject={{
                id: endDateField,
                error: hasError(errors, endDateField),
                message: errorMessage(errors, endDateField),
              }}
            />
          )
        }
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.banner_image_key,
        complexComponent: ({
          language,
          formContext: { watch, setValue, formState: { errors } }
        }) => {
          const bannerImageField = `banner.translations.${language}.coverPhotos.0`;
          return (
            <ReactHookFormImageUploader
              title={'Banner image'}
              uploadSuggest={i18n.t('square_cover_photo_suggest_with_variable', {
                locale: language,
                ...config.bannerSize,
                minWidth: 900,
              })}
              name={bannerImageField}
              minWidth={900}
              minHeight={config.bannerSize.minHeight}
              value={watch(bannerImageField)}
              aspect={900 / config.bannerSize.minHeight}
              maxImageNum={1}
              language={language}
              errorId={`banner.translations.${language}.coverPhotos`}
            />
          )
        },
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.banner_display_order_key,
        component: ReactHookFormTextInput,
        componentProps: {
          valueFieldName: 'banner.order',
          title: 'Display order',
          type: 'number',
          shortDescription: 'Minimum value is 1. The smaller value, the higher priority.',
        },
      },

      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.is_featured_campaign_key,
        component: ReactHookFormSwitch,
        componentProps: {
          title: "Homepage - FEATURED SPECIALS",
          valueFieldName: 'isFeatured',
          titlePopOverImage: HomepageFeatured,
        },
      },

      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.show_in_offer_tab_key,
        component: ReactHookFormSwitch,
        componentProps: {
          title: "Offer tab - campaign categories",
          valueFieldName: 'showInOfferTab',
          titlePopOverImage: OfferTab,
        },
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.campaignListingSection.sectionContent.campaign_category_selector_key,
        component: CategorySelectorWithPrompt,
      },
    ],
  },
}

export const campaignCreationActivePeriodSectionConfig = {
  keys: [
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.activePeriod.sectionContent.active_period_key,
    CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.activePeriod.sectionContent.blackout_period_key,
  ],
  section: {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.activePeriod.sectionKey,
    hideInOtherLanguages: true,
    sectionTitle: 'Active Period',
    sectionTitlePopoverDetail: <ActivePeriodPopoverDetail />,
    description: 'Set when customers can participate in this campaign.',
    descriptionClass: 'tips-message',
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.activePeriod.sectionContent.active_period_key,
        complexComponent: ({ formContext: { watch, setValue, formState: { errors } } }) => (
          <PeriodSection
            startErrorObject={{
              id: 'activeStartDate',
              error: hasError(errors, 'activeStartDate'),
              message: errorMessage(errors, 'activeStartDate'),
            }}
            endErrorObject={{
              id: 'activeEndDate',
              error: hasError(errors, 'activeEndDate'),
              message: errorMessage(errors, 'activeEndDate'),
            }}
            switchButtonTitle="It is an all-time active campaign"
            isAlwaysPeriod={watch("isAlwaysActivePeriod")}
            switchOnChange={(value) => {
              setValue('isAlwaysActivePeriod', value, { shouldDirty: true });
            }}
            startDate={watch('activeStartDate')}
            startDateChange={(date) => {
              setValue('activeStartDate', date, { shouldDirty: true });
            }}
            endDate={watch('activeEndDate')}
            endDateChange={(date) => {
              setValue('activeEndDate', date, { shouldDirty: true });
            }}
          />
        )
      },
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.activePeriod.sectionContent.blackout_period_key,
        complexComponent: ({ formContext: { watch, setValue } }) => (
          <PeriodAndWeekdaySection
            switchButtonTitle="Blackout period"
            periodValuesField="activePeriodValues"
            periodValues={watch('activePeriodValues')}
            periodValuesChange={(data) => {
              setValue('activePeriodValues', data, { shouldDirty: true });
            }}
            weekday={watch('activeWeekday')}
            weekdayChange={(data) => {
              setValue('activeWeekday', data, { shouldDirty: true });
            }}
            maxDate={watch('activeEndDate')}
            minDate={watch('activeStartDate')}
            disabledDate={!watch('isAlwaysActivePeriod')}
          />
        ),
      },
    ],
  },
}

export const campaignCreationRecurringCampaignSections = [
  {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.recurringCampaign.sectionKey,
    hideInOtherLanguages: true,
    sectionTitle: 'Recurring Campaign',
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.recurringCampaign.sectionContent.recurring_birthday_selector_key,
        complexComponent: ({
          formContext: {
            watch,
            setValue,
            formState: { errors },
          },
        }) => {
          const isBirthdayRecurringCampaign = watch('isBirthdayRecurringCampaign');
          return (
            <RecurringCampaignSwitch
              setValue={(isChecked) => {
                setValue("isBirthdayRecurringCampaign", isChecked, { shouldDirty: true })
              }}
              defaultValue={isBirthdayRecurringCampaign}
            />
          );
        },
      },
    ],
  },

  {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.recurringCampaignActivePeriod.sectionKey,
    hideInOtherLanguages: true,
    sectionTitle: 'Recurring active period',
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.recurringCampaignActivePeriod.sectionContent.recurring_birthday_active_period_selector_key,
        component: RecurringCampaignPeriodSelector,
        componentProps: {
          prefix: "recurringDate.active"
        }
      },
    ],
  },
  {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.recurringCampaignVisiblePeriod.sectionKey,
    hideInOtherLanguages: true,
    sectionTitle: 'Recurring visible period',
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.recurringCampaignVisiblePeriod.sectionContent.recurring_birthday_visible_period_selector_key,
        component: RecurringCampaignPeriodSelector,
        componentProps: {
          prefix: "recurringDate.visible"
        }
      },
    ],
  },

  {
    sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.recurringMessage.sectionKey,
    hideInOtherLanguages: true,
    sectionTitle: 'MESSAGE FOR RECURRING CAMPAIGN (optional)',
    sectionContent: [
      {
        key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.recurringMessage.sectionContent.recurring_message_selector_key,
        component: RecurringMessages,
      },
    ],
  },
]

export const CREATE_CAMPAIGN_CONTENT_PAGE_CONFIG = {
  extraValidate: ({ getValues, setError, clearErrors }) => true,
  getDisplayComponentKeys: ({ formContext: { watch } }) => {
    const campaignType = watch('campaignType');
    const earningRuleType = watch('linkedEarningRules.type');
    const displayInFrontend = watch('displayInFrontend');
    const isExclusive = watch('isExclusive');
    const excludeBlacklistSegment = watch('excludeBlacklistSegment');
    const haveHomepageBanner = watch('haveHomepageBanner');
    const showInOfferTab = watch('showInOfferTab');
    const isBirthdayRecurringCampaign = watch('isBirthdayRecurringCampaign');

    const isBirthEarningCampaign = (
      campaignType === CampaignType.earningRulesCampaign
      && earningRuleType === EarningRuleType.birthday
    );
    const showDisplayInFrontend = getShowDisplayInFrontend(campaignType, earningRuleType);
    const targetCustomerSection = campaignCreationGeneralSectionConfig.getDisplayKeys(isExclusive, excludeBlacklistSegment)
    
    let displayLanguageSectionComponents = [
      ...campaignCreationGeneralSectionConfig.keys,
      ...targetCustomerSection,
      ...campaignCreationKeyVisualSectionConfig.getDisplayKeys(isExclusive),
      ...campaignCreationInstructionSectionConfig.keys,
      ...campaignCreationDetailSectionConfig.keys,
      ...campaignCreationCampaignListingSectionConfig.getDisplayKeys(haveHomepageBanner, showInOfferTab),
      ...(ProcessEnvConfig.haveShopMall ? ['visible_mall_key'] : []),
      ...campaignCreationActivePeriodSectionConfig.keys,
      'visible_period_section_key',
      ...(isBirthEarningCampaign ? [] : ['recurring_birthday_selector_key']),
      ...(isBirthdayRecurringCampaign
        ? [
          'recurring_birthday_active_period_selector_key',
          'recurring_birthday_visible_period_selector_key',
          ...(tierTypeConfig.messages ? ['recurring_message_selector_key'] : []),
        ]
        : []
      ),
    ];

    if (
      showDisplayInFrontend
      && !displayInFrontend
      && displayInFrontend !== undefined
    ) {
      displayLanguageSectionComponents = [
        'general_name_key',
        ...targetCustomerSection,
        'active_period_key',
        'blackout_period_key',
        ...(isBirthEarningCampaign ? [] : ['recurring_birthday_selector_key']),
        ...(isBirthdayRecurringCampaign
          ? [
            'recurring_birthday_active_period_selector_key',
            ...(tierTypeConfig.messages ? ['recurring_message_selector_key'] : []),
          ]
          : []
        ),
      ]
    }

    return {
      displayNormalSectionComponents: showDisplayInFrontend ? ['display_in_frontend_key'] : [],
      displayLanguageSectionComponents,
    };
  },
  normalSections: [
    {
      sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.displayInFrontend.sectionKey,
      sectionTitle: '',
      sectionContent: [
        {
          key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.displayInFrontend.sectionContent.display_in_frontend_key,
          component: ReactHookFormSwitch,
          componentProps: {
            title: "DISPLAY THIS CAMPAIGN IN FRONTEND",
            customTitleClass: "campaign-streamline-display-in-frontend",
            valueFieldName: 'displayInFrontend',
            defaultTrue: true,
          },
        }
      ]
    },
  ],
  languageSections: [
    campaignCreationGeneralSectionConfig.section,
    campaignCreationTargetCustomerSectionConfig.section,
    campaignCreationKeyVisualSectionConfig.section,
    campaignCreationInstructionSectionConfig.section,
    campaignCreationDetailSectionConfig.section,
    campaignCreationCampaignListingSectionConfig.section,
    {
      sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.mall.sectionKey,
      hideInOtherLanguages: true,
      sectionContent: [
        {
          key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.mall.sectionContent.visible_mall_key,
          component: VisibleMallSection,
        },
      ],
    },
    campaignCreationActivePeriodSectionConfig.section,
    {
      sectionKey: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.visiblePeriod.sectionKey,
      hideInOtherLanguages: true,
      sectionContent: [
        {
          key: CAMPAIGN_CONTENT_PAGE_ANCHOR_KEY.visiblePeriod.sectionContent.visible_period_section_key,
          component: VisiblePeriodSection,
        },
      ],
    },
    ...campaignCreationRecurringCampaignSections
  ],
};
